import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Grid, IconButton, Button, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText,
    Alert, Snackbar, Tooltip, useMediaQuery, useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Import icons - using direct imports for reliability
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningIcon from '@mui/icons-material/Warning';
import CreditCardOffIcon from '@mui/icons-material/CreditCardOff';

// Import the streamlined Stripe form
import StripePaymentForm from './StripePaymentForm';

// Image Constants - embed SVG directly for reliability
const creditCardIconSvg = `
<svg width="34" height="28" viewBox="0 0 34 28" fill="none" xmlns="http://www.w3.org/2000/svg">
  <rect width="34" height="28" rx="3" fill="#F2F2F2"/>
  <rect x="0.5" y="0.5" width="33" height="27" rx="2.5" stroke="#E5E5E5" stroke-opacity="0.8"/>
  <rect x="3" y="16" width="12" height="2" fill="#CCCCCC"/>
  <rect x="3" y="20" width="6" height="2" fill="#CCCCCC"/>
  <rect x="19" y="20" width="12" height="2" fill="#CCCCCC"/>
  <rect x="3" y="6" width="28" height="4" fill="#CCCCCC"/>
</svg>
`;

// Custom styled components with forceful styling
const ExpiredBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 16,
  left: 16,
  backgroundColor: '#F44336',
  color: 'white',
  padding: '4px 12px',
  borderRadius: '8px',
  fontSize: '0.75rem',
  fontWeight: 600,
  display: 'flex !important',
  alignItems: 'center !important',
  justifyContent: 'center !important',
  gap: '2px',
  zIndex: 3,
  minWidth: '80px',
  textAlign: 'center',
  '& .MuiSvgIcon-root': {
    fontSize: '14px !important',
    marginRight: '2px !important'
  }
}));

const UpdateCardButton = styled(Button)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: '#4CAF50 !important',
  color: 'white !important',
  borderBottomLeftRadius: '16px',
  borderBottomRightRadius: '16px',
  borderTopLeftRadius: 0,
  borderTopRightRadius: 0,
  textTransform: 'none',
  padding: '8px 0',
  '&:hover': {
    backgroundColor: '#388E3C !important',
  }
}));

// Main Payment Methods Component
const PaymentMethods = ({ darkMode, service, getAuthToken }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    
    const [loading, setLoading] = useState(true);
    const [processingCard, setProcessingCard] = useState(null);
    const [error, setError] = useState(null);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(0);
    const [showAddPaymentMethod, setShowAddPaymentMethod] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [cardToDelete, setCardToDelete] = useState(null);
    const [showUpdateCardDialog, setShowUpdateCardDialog] = useState(false);
    const [cardToUpdate, setCardToUpdate] = useState(null);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    // Check if a card has expired
    const isCardExpired = (expiry) => {
        if (!expiry) return false;

        const [month, year] = expiry.split('/');
        const expiryDate = new Date(2000 + parseInt(year), parseInt(month) - 1, 1);
        expiryDate.setMonth(expiryDate.getMonth() + 1); // Last day of expiry month
        expiryDate.setDate(0);

        const today = new Date();
        return expiryDate < today;
    };

    // Check if any card is expired
    const hasExpiredCards = () => {
        return paymentMethods.some(card => isCardExpired(card.expiry));
    };

    // Fetch payment methods
    useEffect(() => {
        fetchPaymentMethods();
    }, []);

    const fetchPaymentMethods = async () => {
        try {
            setLoading(true);
            
            // Get auth token
            const token = await getAuthToken();
            
            // Use payment service to fetch payment methods
            const response = await service.getPaymentMethods(token);
            setPaymentMethods(response.data);

            // Find default payment method index
            const defaultIndex = response.data.findIndex(method => method.isDefault);
            if (defaultIndex >= 0) {
                setSelectedPaymentMethod(defaultIndex);
            }

            // Show warning if there are expired cards
            if (response.data.some(card => isCardExpired(card.expiry))) {
                setSnackbar({
                    open: true,
                    message: 'You have one or more expired payment methods that need to be updated',
                    severity: 'warning'
                });
            }
        } catch (err) {
            console.error('Error fetching payment methods:', err);
            setError(err.friendlyMessage || 'Failed to fetch payment methods. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Handle update card
    const handleUpdateCard = (cardId) => {
        setCardToUpdate(cardId);
        setShowUpdateCardDialog(true);
    };

    // Process card update
    const processCardUpdate = async (paymentMethodId) => {
        try {
            setProcessingCard(cardToUpdate);
            
            // Get auth token
            const token = await getAuthToken();
            
            // Use payment service to update payment method
            await service.updatePaymentMethod({
                payment_method_id: cardToUpdate,
                new_payment_method_id: paymentMethodId
            }, token);

            setShowUpdateCardDialog(false);

            // Refresh payment methods
            await fetchPaymentMethods();

            setSnackbar({
                open: true,
                message: 'Payment method updated successfully',
                severity: 'success'
            });
        } catch (err) {
            console.error('Error updating payment method:', err);
            setSnackbar({
                open: true,
                message: err.friendlyMessage || 'Failed to update payment method',
                severity: 'error'
            });
        } finally {
            setProcessingCard(null);
            setCardToUpdate(null);
        }
    };

    // Add a new payment method via Stripe
    const handleAddPaymentSuccess = async (paymentMethodId, setAsDefault) => {
        try {
            // Use processingCard state to show global loading
            setProcessingCard('adding');
            
            // Get auth token
            const token = await getAuthToken();
            
            // Use payment service to add payment method
            await service.addPaymentMethod({
                payment_method_id: paymentMethodId,
                set_as_default: setAsDefault
            }, token);

            // After successful API call, refresh the payment methods
            await fetchPaymentMethods();
            setShowAddPaymentMethod(false);

            // Show success message
            setSnackbar({
                open: true,
                message: 'Payment method added successfully',
                severity: 'success'
            });
        } catch (err) {
            console.error('Error adding payment method:', err);
            setSnackbar({
                open: true,
                message: err.friendlyMessage || 'Failed to add payment method. Please try again.',
                severity: 'error'
            });
        } finally {
            setProcessingCard(null);
        }
    };

    // Handle Stripe form error
    const handleAddPaymentError = (errorMessage) => {
        setSnackbar({
            open: true,
            message: errorMessage || 'Failed to add payment method',
            severity: 'error'
        });
    };

    // Set a payment method as default
    const handleSetDefaultPaymentMethod = async (paymentMethodId) => {
        setProcessingCard(paymentMethodId);

        try {
            // Get auth token
            const token = await getAuthToken();
            
            // Use payment service to set default payment method
            await service.setDefaultPaymentMethod(paymentMethodId, token);

            // Refresh payment methods to reflect new default
            await fetchPaymentMethods();

            setSnackbar({
                open: true,
                message: 'Default payment method updated successfully',
                severity: 'success'
            });
        } catch (err) {
            console.error('Error updating default payment method:', err);
            setSnackbar({
                open: true,
                message: err.friendlyMessage || 'Failed to update default payment method',
                severity: 'error'
            });
        } finally {
            setProcessingCard(null);
        }
    };

    // Delete a payment method
    const handleDeletePaymentMethod = async () => {
        if (!cardToDelete) return;

        setProcessingCard(cardToDelete);
        setOpenDeleteDialog(false);

        try {
            // Get auth token
            const token = await getAuthToken();
            
            // Use payment service to delete payment method
            await service.deletePaymentMethod(cardToDelete, token);

            // Refresh payment methods list
            await fetchPaymentMethods();

            setSnackbar({
                open: true,
                message: 'Payment method deleted successfully',
                severity: 'success'
            });
        } catch (err) {
            console.error('Error deleting payment method:', err);
            setSnackbar({
                open: true,
                message: err.friendlyMessage || 'Failed to delete payment method',
                severity: 'error'
            });
        } finally {
            setProcessingCard(null);
            setCardToDelete(null);
        }
    };

    // Open delete confirmation dialog
    const confirmDeleteCard = (paymentMethodId) => {
        setCardToDelete(paymentMethodId);
        setOpenDeleteDialog(true);
    };

    // Close snackbar
    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // Update all expired cards
    const handleUpdateAllExpiredCards = () => {
        // Find first expired card
        const expiredCard = paymentMethods.find(card => isCardExpired(card.expiry));
        if (expiredCard) {
            handleUpdateCard(expiredCard.id);
        }
    };

    // Payment Card Component (internal) - UPDATED with forceful styling for expired badge and update button
    const PaymentCard = ({
        method,
        isDefault,
        last4,
        expiry,
        selected,
        onSelect,
        onDelete,
        onSetDefault,
        onUpdateCard,
        id
    }) => {
        // Format the card number for display with proper spacing
        const formattedCardNumber = `•••• •••• •••• ${last4}`;

        // Check if card is expired
        const cardExpired = isCardExpired(expiry);

        return (
            <Box
                onClick={() => !cardExpired && onSelect()}
                sx={{
                    p: { xs: 2.5, sm: 3 },
                    borderRadius: '16px',
                    border: cardExpired
                        ? '2px solid #F44336'
                        : selected
                            ? '2px solid #FF4775'
                            : '1px solid rgba(0,0,0,0.06)',
                    backgroundColor: darkMode
                        ? cardExpired
                            ? 'rgba(244, 67, 54, 0.1)'
                            : isDefault
                                ? 'rgba(255, 71, 117, 0.12)'
                                : 'rgba(32, 33, 44, 0.5)'
                        : cardExpired
                            ? 'rgba(244, 67, 54, 0.05)'
                            : isDefault
                                ? 'rgba(255, 71, 117, 0.05)'
                                : 'white',
                    position: 'relative',
                    cursor: cardExpired ? 'default' : processingCard === id ? 'wait' : 'pointer',
                    transition: 'all 0.2s ease',
                    mb: 2,
                    height: { xs: '150px', sm: '160px' },
                    '&:hover': {
                        boxShadow: cardExpired 
                            ? '0 4px 12px rgba(244, 67, 54, 0.1)' 
                            : darkMode 
                                ? '0 4px 12px rgba(0, 0, 0, 0.2)' 
                                : '0 4px 12px rgba(0, 0, 0, 0.05)',
                    }
                }}
            >
                {/* Card Brand */}
                <Typography variant="h6" fontWeight={600} color={darkMode ? "white" : "#333"}>
                    {method.charAt(0).toUpperCase() + method.slice(1)}
                </Typography>

                {/* Default Badge */}
                {isDefault && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 16,
                            right: 16,
                            backgroundColor: '#FF4775',
                            color: 'white',
                            py: 0.5,
                            px: 1.5,
                            borderRadius: '8px',
                            fontSize: '0.75rem',
                            fontWeight: 600
                        }}
                    >
                        Default
                    </Box>
                )}

                {/* Expired Card Badge - USING CUSTOM STYLED COMPONENT FOR FORCEFUL CENTERING */}
                {cardExpired && (
                    <ExpiredBadge>
                        <WarningIcon />
                        <span>Expired</span>
                    </ExpiredBadge>
                )}

                {/* Selected Indicator (Red checkmark) */}
                {selected && processingCard !== id && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: cardExpired ? 48 : 16,
                            left: 16,
                            zIndex: 2
                        }}
                    >
                        <CheckCircleIcon sx={{ color: '#FF4775', fontSize: 24 }} />
                    </Box>
                )}

                {/* Card Number */}
                <Typography
                    variant="body1"
                    sx={{
                        fontFamily: 'monospace',
                        letterSpacing: '0.5px',
                        my: 1,
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                        color: darkMode ? "white" : "#333"
                    }}
                >
                    {formattedCardNumber}
                </Typography>

                {/* Expiry Date with warning for expired cards */}
                <Typography
                    variant="body2"
                    color={cardExpired ? "error" : darkMode ? "rgba(255,255,255,0.7)" : "text.secondary"}
                    sx={{
                        fontWeight: cardExpired ? 600 : 400,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: { xs: '0.8rem', sm: '0.875rem' }
                    }}
                >
                    Expires {expiry}
                    {cardExpired && (
                        <Typography
                            component="span"
                            color="error"
                            sx={{ 
                                fontWeight: 600, 
                                ml: 1,
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                display: { xs: 'none', sm: 'inline' }
                            }}
                        >
                            Please update your card
                        </Typography>
                    )}
                </Typography>

                {/* Credit Card Image */}
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: cardExpired ? 60 : 16, // Move up if we have the update button
                        right: 16,
                        width: 34,
                        height: 28,
                        opacity: cardExpired ? 0.5 : 1,
                        filter: darkMode ? 'brightness(0.8)' : 'none'
                    }}
                    dangerouslySetInnerHTML={{
                        __html: creditCardIconSvg
                    }}
                />

                {/* Action Buttons for non-default cards */}
                {!isDefault && processingCard !== id && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 16,
                            right: cardExpired ? 16 : 16,
                            display: 'flex',
                            gap: '8px'
                        }}
                    >
                        {/* Set as Default Button - with tooltip */}
                        <Tooltip title="Set as default payment method" arrow placement="top">
                            <IconButton
                                aria-label="Set as default payment method"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onSetDefault(id);
                                }}
                                size="small"
                                disabled={cardExpired}
                                sx={{
                                    backgroundColor: darkMode 
                                        ? 'rgba(255, 71, 117, 0.15)' 
                                        : 'rgba(255, 71, 117, 0.05)',
                                    width: { xs: 28, sm: 32 },
                                    height: { xs: 28, sm: 32 },
                                    p: 0,
                                    '&.Mui-disabled': {
                                        backgroundColor: darkMode 
                                            ? 'rgba(255,255,255,0.05)' 
                                            : 'rgba(0,0,0,0.05)',
                                        opacity: 0.5
                                    }
                                }}
                            >
                                <RadioButtonUncheckedIcon sx={{ 
                                    fontSize: { xs: 18, sm: 20 }, 
                                    color: cardExpired 
                                        ? darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)' 
                                        : '#FF4775' 
                                }} />
                            </IconButton>
                        </Tooltip>

                        {/* Delete Button - with tooltip */}
                        <Tooltip title="Delete payment method" arrow placement="top">
                            <IconButton
                                aria-label="Delete payment method"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete(id);
                                }}
                                size="small"
                                sx={{
                                    backgroundColor: darkMode 
                                        ? 'rgba(244, 67, 54, 0.15)' 
                                        : 'rgba(244, 67, 54, 0.05)',
                                    width: { xs: 28, sm: 32 },
                                    height: { xs: 28, sm: 32 },
                                    p: 0
                                }}
                            >
                                <DeleteOutlineIcon sx={{ 
                                    fontSize: { xs: 18, sm: 20 }, 
                                    color: '#F44336' 
                                }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}

                {/* Update Card Button for expired cards - USING CUSTOM STYLED COMPONENT FOR GREEN COLOR */}
                {cardExpired && (
                    <UpdateCardButton
                        variant="contained"
                        fullWidth
                        onClick={(e) => {
                            e.stopPropagation();
                            onUpdateCard(id);
                        }}
                    >
                        Update Card
                    </UpdateCardButton>
                )}

                {/* Processing Overlay */}
                {processingCard === id && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: darkMode 
                                ? 'rgba(32, 33, 44, 0.8)' 
                                : 'rgba(255, 255, 255, 0.8)',
                            borderRadius: '16px',
                            zIndex: 10
                        }}
                    >
                        <Box display="flex" flexDirection="column" alignItems="center">
                            <CircularProgress size={32} sx={{ color: '#FF4775', mb: 1 }} />
                            <Typography variant="body2" color={darkMode ? "white" : "#333"} fontWeight={500}>
                                Processing...
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Box>
        );
    };

    return (
        <Box sx={{ mb: 4 }}>
            {/* Expired Cards Alert Banner */}
            {hasExpiredCards() && (
                <Alert
                    severity="warning"
                    variant="filled"
                    icon={<CreditCardOffIcon />}
                    sx={{
                        mb: 3,
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center'
                    }}
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            variant="outlined"
                            onClick={handleUpdateAllExpiredCards}
                            sx={{
                                borderColor: 'rgba(255,255,255,0.5)',
                                '&:hover': { borderColor: 'white' }
                            }}
                        >
                            Update All
                        </Button>
                    }
                >
                    You have one or more expired payment methods that need to be updated
                </Alert>
            )}

            {/* Header and Add Card Button - Responsive layout */}
            <Box
                display="flex"
                flexDirection={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                mb={3}
                gap={2}
            >
                <Typography 
                    variant="h5" 
                    fontWeight={700} 
                    color={darkMode ? "white" : "#333"}
                    sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                >
                    Your Payment Methods
                </Typography>

                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => !processingCard && setShowAddPaymentMethod(!showAddPaymentMethod)}
                    disabled={!!processingCard}
                    fullWidth={isMobile}
                    sx={{
                        backgroundColor: '#FF4775',
                        color: 'white',
                        borderRadius: '50px',
                        textTransform: 'none',
                        px: { xs: 2, sm: 3 },
                        py: { xs: 1, sm: 1.5 },
                        fontWeight: 600,
                        fontSize: { xs: '0.875rem', sm: '0.95rem' },
                        '&:hover': {
                            backgroundColor: '#ff6189',
                        },
                        '&.Mui-disabled': {
                            backgroundColor: '#FF4775',
                            opacity: 0.7,
                            color: 'white'
                        }
                    }}
                >
                    Add Payment Method
                </Button>
            </Box>

            {/* Loading State */}
            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" p={6}>
                    <CircularProgress size={40} sx={{ color: '#FF4775' }} />
                </Box>
            ) : error ? (
                <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                    <Typography color="error">{error}</Typography>
                </Box>
            ) : (
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                    {paymentMethods.length > 0 ? (
                        paymentMethods.map((method, index) => (
                            <Grid item xs={12} sm={6} md={4} key={method.id}>
                                <PaymentCard
                                    id={method.id}
                                    method={method.method}
                                    last4={method.last4}
                                    expiry={method.expiry}
                                    isDefault={method.isDefault}
                                    selected={selectedPaymentMethod === index}
                                    onSelect={() => !processingCard && setSelectedPaymentMethod(index)}
                                    onDelete={confirmDeleteCard}
                                    onSetDefault={handleSetDefaultPaymentMethod}
                                    onUpdateCard={handleUpdateCard}
                                />
                            </Grid>
                        ))
                    ) : (
                        <Grid item xs={12}>
                            <Box
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                justifyContent="center"
                                p={{ xs: 4, sm: 6 }}
                                sx={{
                                    border: `1px dashed ${darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.12)'}`,
                                    borderRadius: '16px',
                                    backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.01)'
                                }}
                            >
                                <Box
                                    dangerouslySetInnerHTML={{
                                        __html: creditCardIconSvg
                                    }}
                                    sx={{ 
                                        mb: 2, 
                                        transform: 'scale(1.5)',
                                        filter: darkMode ? 'brightness(0.8)' : 'none'
                                    }}
                                />
                                <Typography 
                                    variant="h6" 
                                    color={darkMode ? "white" : "#333"} 
                                    fontWeight={600} 
                                    gutterBottom
                                    sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
                                >
                                    No payment methods found
                                </Typography>
                                <Typography 
                                    variant="body2" 
                                    color={darkMode ? "rgba(255,255,255,0.7)" : "text.secondary"} 
                                    textAlign="center" 
                                    mb={3}
                                >
                                    Add a payment method to manage your subscriptions.
                                </Typography>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => !processingCard && setShowAddPaymentMethod(true)}
                                    disabled={!!processingCard}
                                    sx={{
                                        backgroundColor: '#FF4775',
                                        color: 'white',
                                        borderRadius: '50px',
                                        textTransform: 'none',
                                        px: 3,
                                        py: 1,
                                        fontWeight: 600,
                                        '&:hover': {
                                            backgroundColor: '#ff6189',
                                        }
                                    }}
                                >
                                    Add Payment Method
                                </Button>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            )}

            {/* Add Payment Method Form */}
            {showAddPaymentMethod && (
                <Box mt={4} mb={4}>
                    <Box
                        sx={{
                            border: darkMode 
                                ? '1px solid rgba(255,255,255,0.1)' 
                                : '1px solid rgba(0,0,0,0.1)',
                            borderRadius: '16px',
                            backgroundColor: darkMode ? 'rgba(32, 33, 44, 0.5)' : 'white',
                            overflow: 'hidden',
                            boxShadow: darkMode 
                                ? '0 4px 12px rgba(0, 0, 0, 0.2)' 
                                : '0 4px 12px rgba(0, 0, 0, 0.05)'
                        }}
                    >
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            p={2}
                            sx={{
                                borderBottom: darkMode 
                                    ? '1px solid rgba(255,255,255,0.1)' 
                                    : '1px solid rgba(0,0,0,0.1)'
                            }}
                        >
                            <Typography 
                                variant="h6" 
                                fontWeight={600} 
                                color={darkMode ? "white" : "#333"}
                                sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
                            >
                                Add New Payment Method
                            </Typography>
                            <Tooltip title="Close" arrow>
                                <IconButton
                                    onClick={() => setShowAddPaymentMethod(false)}
                                    disabled={processingCard === 'adding'}
                                    sx={{
                                        borderRadius: '8px',
                                        backgroundColor: darkMode 
                                            ? 'rgba(255,255,255,0.1)' 
                                            : 'rgba(0,0,0,0.05)'
                                    }}
                                >
                                    <CloseIcon sx={{ color: darkMode ? "white" : "inherit" }} />
                                </IconButton>
                            </Tooltip>
                        </Box>

                        <Box p={{ xs: 2, sm: 3 }} position="relative">
                            {processingCard === 'adding' && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: darkMode 
                                            ? 'rgba(32, 33, 44, 0.8)' 
                                            : 'rgba(255, 255, 255, 0.8)',
                                        zIndex: 10
                                    }}
                                >
                                    <Box display="flex" flexDirection="column" alignItems="center">
                                        <CircularProgress size={40} sx={{ color: '#FF4775', mb: 1 }} />
                                        <Typography variant="body2" color={darkMode ? "white" : "#333"} fontWeight={500}>
                                            Processing...
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            <StripePaymentForm
                                onSuccess={handleAddPaymentSuccess}
                                onCancel={() => setShowAddPaymentMethod(false)}
                                onError={handleAddPaymentError}
                                darkMode={darkMode} // Pass actual dark mode to StripePaymentForm
                                hideTestInfo={true}
                                isLoading={processingCard === 'adding'}
                            />
                        </Box>
                    </Box>
                </Box>
            )}

            {/* Update Card Dialog */}
            <Dialog
                open={showUpdateCardDialog}
                onClose={() => !processingCard && setShowUpdateCardDialog(false)}
                PaperProps={{
                    style: {
                        borderRadius: '16px',
                        padding: '8px',
                        backgroundColor: darkMode ? '#1e1e28' : 'white'
                    }
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ 
                    fontWeight: 600, 
                    color: darkMode ? "white" : "#333",
                    paddingTop: '16px'
                }}>
                    Update Payment Method
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ 
                        mb: 3,
                        color: darkMode ? "rgba(255,255,255,0.7)" : "inherit" 
                    }}>
                        Please enter your new card details to update this payment method.
                    </DialogContentText>

                    {processingCard === cardToUpdate ? (
                        <Box display="flex" justifyContent="center" p={4}>
                            <CircularProgress size={40} sx={{ color: '#FF4775' }} />
                        </Box>
                    ) : (
                        <StripePaymentForm
                            onSuccess={(paymentMethodId) => processCardUpdate(paymentMethodId)}
                            onCancel={() => setShowUpdateCardDialog(false)}
                            onError={handleAddPaymentError}
                            darkMode={darkMode}
                            hideTestInfo={true}
                            buttonText="Update Card"
                        />
                    )}
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={openDeleteDialog}
                onClose={() => !processingCard && setOpenDeleteDialog(false)}
                PaperProps={{
                    style: {
                        borderRadius: '16px',
                        padding: '8px',
                        backgroundColor: darkMode ? '#1e1e28' : 'white'
                    }
                }}
            >
                <DialogTitle sx={{ 
                    fontWeight: 600, 
                    color: darkMode ? "white" : "#333" 
                }}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <ErrorOutlineIcon sx={{ color: '#F44336' }} />
                        Delete Payment Method
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText 
                        sx={{ color: darkMode ? "rgba(255,255,255,0.7)" : "inherit" }}
                    >
                        Are you sure you want to delete this payment method? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ padding: 2 }}>
                    <Button
                        onClick={() => setOpenDeleteDialog(false)}
                        disabled={!!processingCard}
                        sx={{
                            color: darkMode ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.6)",
                            borderRadius: '10px',
                            textTransform: 'none'
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={handleDeletePaymentMethod}
                        disabled={!!processingCard}
                        sx={{
                            backgroundColor: '#F44336',
                            borderRadius: '10px',
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: '#d32f2f',
                            }
                        }}
                    >
                        {processingCard === cardToDelete ? (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                                Deleting...
                            </Box>
                        ) : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Success/Error notification */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ borderRadius: '10px' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default PaymentMethods;